import { NEW_COURSE_PRICE } from "../../utils/coursePricing";
import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import DashBoardSidebar from "../../layouts/dashBoard-Sidebar";
import DashboardNavbar from "../../layouts/dashboardNavbar";
import { exportGladeInvoice, getGladePendingBilling } from "../../api/server";
import BillingSourceTabs from "./BillingSourceTabs";
import BillingFilterDropdown from "./BillingFilterDropdown";
import "./BillingWorkspace.css";

const defaultAmount = NEW_COURSE_PRICE;

const GladeBilling = ({ embedded = false }) => {
  const [response, setResponse] = useState({ CC: [], DE: [] });
  const [courseFilter, setCourseFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [billingBasis, setBillingBasis] = useState("completion");
  const [courseAmount, setCourseAmount] = useState(String(defaultAmount));
  const [serviceFee, setServiceFee] = useState("3");
  const [credit, setCredit] = useState("0");
  const [loading, setLoading] = useState(true);
  const [exportType, setExportType] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getGladePendingBilling(courseFilter, monthFilter, billingBasis);
      const pending = result?.data?.pendingBilling || {};
      const withAmounts = { CC: [], DE: [] };
      Object.entries(withAmounts).forEach(([course]) => {
        withAmounts[course] = (pending[course] || []).map((student) => ({
          ...student,
          amount: String(defaultAmount),
        }));
      });
      setCourseAmount(String(defaultAmount));
      setResponse(withAmounts);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to fetch Glade billing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseFilter, monthFilter, billingBasis]);

  const visibleCourses = useMemo(
    () => Object.entries(response).filter(([, students]) => students.length > 0),
    [response]
  );
  const totalCourses = useMemo(
    () => visibleCourses.reduce(
      (total, [, students]) => total + students.reduce(
        (courseTotal, student) => courseTotal + Number(student.amount || 0),
        0
      ),
      0
    ),
    [visibleCourses]
  );
  const serviceAmount = totalCourses * (Number(serviceFee || 0) / 100);
  const grandTotal = totalCourses + serviceAmount - Number(credit || 0);

  const handleAmountChange = (course, index, value) => {
    if (!/^\d*\.?\d{0,2}$/.test(value)) return;
    setResponse((current) => ({
      ...current,
      [course]: current[course].map((student, studentIndex) =>
        studentIndex === index ? { ...student, amount: value } : student
      ),
    }));
  };

  const applyAmountToAll = (value) => {
    if (!/^\d*\.?\d{0,2}$/.test(value)) return;
    setCourseAmount(value);
    setResponse((current) => Object.fromEntries(
      Object.entries(current).map(([course, students]) => [
        course,
        students.map((student) => ({ ...student, amount: value })),
      ])
    ));
  };

  const holdStudent = (course, index) => {
    setResponse((current) => ({
      ...current,
      [course]: current[course].filter((_, studentIndex) => studentIndex !== index),
    }));
  };

  const handleExport = async (type) => {
    const students = Object.fromEntries(
      visibleCourses.map(([course, records]) => [
        course,
        {
          data: records.map((student) => ({
            id: student.id,
            name: student.name,
            firstName: student.firstName,
            lastName: student.lastName,
            email: student.email,
            attorneyCode: student.attorneyCode,
            attorneyName: student.attorneyName,
            courseType: course === "CC" ? "Pre-Filing" : "Post-Filing",
            completionDate: student.completionDate,
            enrolDate: billingBasis === "completion"
              ? student.completionDate
              : student.enrolDate,
            amount: `$${Number(student.amount || 0).toFixed(2)}`,
          })),
        },
      ])
    );
    const hasCC = Boolean(students.CC?.data?.length);
    const hasDE = Boolean(students.DE?.data?.length);
    const course = hasCC && hasDE ? "both" : hasCC ? "first" : "second";

    setExportType(type);
    try {
      const result = await exportGladeInvoice({
        type,
        course,
        attorneyName: "Glade",
        providerName: "Evergreen Financial Counseling",
        billingBasis,
        billingMonth: (monthFilter === "previous"
          ? moment().subtract(1, "month")
          : moment()
        ).format("MMMM YYYY"),
        totalCourse: `$${totalCourses.toFixed(2)}`,
        serviceAmount: `$${serviceAmount.toFixed(2)}`,
        servicePercentage: serviceFee,
        total: `$${grandTotal.toFixed(2)}`,
        credit: Number(credit || 0),
        students,
      });
      if (result?.data?.success) {
        toast.success("Glade invoice generated successfully!");
        window.open(result.data.data, "_blank", "noopener,noreferrer");
        await fetchData();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Unable to generate Glade invoice");
    } finally {
      setExportType("");
    }
  };

  const noStudents = visibleCourses.length === 0;
  const exportDisabled = noStudents || grandTotal <= 0 || Boolean(exportType);

  const content = (
    <div className="glade-billing-content">
      <div className="glade-billing-toolbar">
        <div className="glade-billing-filters">
          <BillingFilterDropdown
            id="glade-course-filter"
            label="Course"
            icon="fa-solid fa-graduation-cap"
            value={courseFilter}
            onChange={setCourseFilter}
            options={[
              { value: "all", label: "All" },
              { value: "first", label: "CC" },
              { value: "second", label: "DE" },
            ]}
          />
          <BillingFilterDropdown
            id="glade-month-filter"
            label="Month"
            icon="fa-solid fa-calendar-days"
            value={monthFilter}
            onChange={setMonthFilter}
            options={[
              { value: "all", label: "All" },
              { value: "current", label: "Current" },
              { value: "previous", label: "Previous" },
            ]}
          />
          <BillingFilterDropdown
            id="glade-billing-basis-filter"
            label="Invoice"
            icon="fa-solid fa-file-invoice-dollar"
            value={billingBasis}
            onChange={setBillingBasis}
            options={[
              { value: "enrollment", label: "Enrollment" },
              { value: "completion", label: "Completion" },
            ]}
          />
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "150px" }}>
          <ThreeDots height="20" width="52" color="#4fa94d" />
        </div>
      ) : noStudents ? (
        <div className="billing-empty-state">No pending Glade billing found</div>
      ) : visibleCourses.map(([course, students]) => (
        <div key={course} className="billing-course-card">
          <h3 className="billing-course-title">{course} Students</h3>
          <div className="billing-table-scroll">
                  <table className="table glade-billing-table">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Email</th>
                        <th className="text-center">Course Percentage</th>
                        <th>{billingBasis === "completion" ? "Completion Date" : "Enroll Date"}</th>
                        <th>Amount</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student, index) => (
                        <tr key={student.id}>
                          <td className="billing-student-name">{student.name}</td>
                          <td>{student.email}</td>
                          <td className="text-center">{Number(student.percentage || 0)}%</td>
                          <td>
                            {(() => {
                              const date = billingBasis === "completion"
                                ? student.completionDate
                                : student.enrolDate;
                              return date && moment(date).isValid()
                                ? moment(date).format("MMMM Do YYYY")
                                : "—";
                            })()}
                          </td>
                          <td>
                            <div className="billing-amount-control">
                              <span>$</span>
                              <input
                                className="form-control"
                                aria-label={`${student.name} amount`}
                                value={student.amount}
                                onChange={(event) => handleAmountChange(course, index, event.target.value)}
                              />
                            </div>
                          </td>
                          <td>
                            <button
                              className="billing-hold-button"
                              onClick={() => holdStudent(course, index)}
                            >
                              Hold
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
          </div>
        </div>
      ))}

      {!loading && !noStudents && (
        <div className="billing-summary-card">
          <div className="billing-summary-row">
            <span className="billing-summary-label">Apply Amount to All</span>
            <div className="billing-amount-control">
              <span>$</span>
                    <input
                      className="form-control"
                      aria-label="Apply amount to all students"
                      value={courseAmount}
                      onChange={(event) => applyAmountToAll(event.target.value)}
                    />
            </div>
          </div>
          <div className="billing-summary-row">
            <span className="billing-summary-label">Total Courses Amount</span>
            <span className="billing-summary-value">${totalCourses.toFixed(2)}</span>
          </div>
          <div className="billing-summary-row billing-summary-editable">
            <div className="d-flex align-items-center gap-2">
              <span className="billing-summary-label">Service Fee</span>
              <div className="d-flex align-items-center gap-2">
                <div className="billing-amount-control">
                  <input
                      className="form-control"
                      aria-label="Service fee percentage"
                      value={serviceFee}
                      onChange={(event) => /^\d*\.?\d{0,2}$/.test(event.target.value) && setServiceFee(event.target.value)}
                  />
                </div>
                <span>%</span>
              </div>
            </div>
            <span className="billing-summary-value">${serviceAmount.toFixed(2)}</span>
          </div>
          <div className="billing-summary-row billing-summary-editable">
            <div className="d-flex align-items-center gap-2">
              <span className="billing-summary-label">Credit</span>
              <div className="billing-amount-control">
                <span>$</span>
                    <input
                      className="form-control"
                      aria-label="Credit amount"
                      value={credit}
                      onChange={(event) => /^\d*\.?\d{0,2}$/.test(event.target.value) && setCredit(event.target.value)}
                    />
              </div>
            </div>
            <span className="billing-summary-value">-${Number(credit || 0).toFixed(2)}</span>
          </div>
          <div className="billing-summary-row billing-summary-total">
            <span className="billing-summary-label">Total</span>
            <span className="billing-summary-value">${grandTotal.toFixed(2)}</span>
          </div>
          <div className="billing-export-actions">
                  <button className="btn_brnd" disabled={exportDisabled} onClick={() => handleExport("excel")}>
                    {exportType === "excel" ? <ThreeDots height="18" width="52" color="#4e9c4b" wrapperStyle={{ display: "flex", justifyContent: "center", alignItems: "center" }} /> : "Export Invoice in Excel"}
                  </button>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="wrapper">
      <DashBoardSidebar />
      <div id="content">
        <DashboardNavbar />
        <section className="manage-students language_wrap invoice-wraper billing-workspace">
          <BillingSourceTabs activeSource="glade" view="billing" />
          {content}
        </section>
      </div>
    </div>
  );
};

export default GladeBilling;
